import { EuiCheckbox } from "@elastic/eui";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useSelector } from "react-redux";
import { StyledLabel, StyledRequired } from "components/styles";
import trackAnalytics from "trackAnalytics";
import changedForm from "hooks/useChangedForm";
import { Button, Input, SelectWithSearch, TextArea } from "components";
import checkAuthorization from "modules/auth/helpers/checkAuthorization";
import { authorizedActionsSelector } from "modules/auth/slice";
import Info from "./Info";
import { mapLocationToAPI } from "../mapUiToData";
import { mapDataToDropdown } from "../mapOptionsToUi";
import {
    addNewLocation,
    checkLocationMatches,
    editLocation,
    fetchCategories,
    fetchOrganizations,
} from "../api";
import GoogleMapSearchComponent from "./GoogleMapSearch";
import GoogleMapComponent from "./GoogleMapComponent";
import useGoogleMapsLoader from "hooks/useGoogleMapsLoader";

const Container = styled.div`
    display: flex;
    flex-direction: column;
    max-height: 100%;
`;

const ErrorsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-top: 5px;
`;

const ErrorMessage = styled.div`
    width: 100%;
    background-color: #f8e9e9;
    padding: 16px;
    color: #bd271e;
    font-weight: 500;
    font-size: 16px;
    display: flex;
    gap: 9px;
    align-items: center;

    svg {
        width: 14px;
        height: 14px;
        color: #bd271e;
    }
`;

const Body = styled.div`
    display: flex;
    gap: 20px;
`;

const Footer = styled.footer`
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    position: sticky;
    bottom: 0;
    padding: 20px 0px;
    background-color: white;
    z-index: 8;
`;

const ButtonsContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: flex-end;
    flex-wrap: nowrap;
    align-items: center;
    font-weight: 500;
    font-size: 14px;
`;

const InputContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const TitleContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const LocationDetails = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;

    svg {
        width: 14px;
        height: 14px;
        color: #000507;
        cursor: pointer;
    }
`;

const LocationId = styled.div`
    background: #e5f0fc;
    border-radius: 3px;
    padding: 7px 40px;
    color: #006bb4;
    font-weight: 600;
    font-size: 14px;
`;

const IconsContainer = styled.div`
    display: flex;
    gap: 17px;
`;

const Title = styled.h3`
    font-size: 18px;
    font-weight: 700;
    color: #198aa5;
    margin-bottom: 4px;
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 30px;
    border: none;
    flex: 1;
`;

const SearchTagsContainer = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
    font-weight: 400;
    font-size: 10px;
    color: #c4c4c4;
    white-space: nowrap;
`;

const SearchTag = styled.div`
    padding: 3px 10px;
    background-color: #fbfcfd;
    font-size: 12px;
    color: #6b7280;
`;

const TagsContainer = styled.div`
    display: flex;
    justify-content: space-between;
`;

const Tag = styled.div`
    padding: 10px;
    border: 1px solid #e2e3e4;
    border-radius: 6px;
    min-width: 200px;
    cursor: pointer;
`;

const MapContainer = styled.div`
    flex: 2;
    height: 600px;
    position: sticky;
    top: 0;
`;

function CreateLocation({
    onCancel,
    isEdit,
    existingLocationData,
    onSuccess,
    readOnly,
    onSwitchToEdit,
}) {
    const [allClients, setAllClients] = useState([]);
    const [allLocationCategories, setAllLocationCategories] = useState([]);
    const [locationData, setLocationData] = useState({
        id: null,
        client: null,
        locationName: "",
        googleLocationDetails: "",
        nameAr: "",
        nameEn: "",
        street: "",
        description: "",
        category: null,
        clientCode: "",
        tags: [],
        lat: 0,
        lang: 0,
        gov: "",
        city: "",
        area: "",
    });
    const [similarArabicLoactions, setSimilarArabicLocations] = useState([]);
    const [similarEnglishLocations, setSimilarEnglishLocations] = useState([]);
    const [isFormValid, setIsFormValid] = useState(false);
    const [formErrors, setFormErrors] = useState([]);
    // the address coming from google search component is more accurate than gecoding so we use this flag
    const [isLocationSelectedFromSearch, setIsLocationSelectedFromSearch] = useState(false);
    const authorizedActions = useSelector(authorizedActionsSelector());
    const isAuthorizedToEdit = checkAuthorization(authorizedActions, "locations", "update");
    const handleSetMapLocationData = (address, gov, city, area, lat, lng) => {
        setLocationData({
            ...locationData,
            ...(address && { googleLocationDetails: address }),
            gov,
            city,
            area,
            lat,
            lang: lng,
        });
    };
    const { isLoaded } = useGoogleMapsLoader();

    console.log("====> existingLocationData", existingLocationData)
    const handleSearchInputData = (address, lat, lang, name) => {
        setIsLocationSelectedFromSearch(true);
        setLocationData({
            ...locationData,
            lat,
            lang,
            locationName: name,
            googleLocationDetails: address,
        });
    };

    const handleClearLocation = () => {
        setLocationData({
            ...locationData,
            locationName: "",
            googleLocationDetails: "",
        });
    };

    const handleLocationDetailsChange = (e) => {
        const { name, value } = e.target;
        setLocationData({
            ...locationData,
            [name]: value,
        });
    };

    const handleLocationTagChange = (name) => {
        if (locationData.tags.includes(name)) {
            setLocationData({
                ...locationData,
                tags: locationData.tags.filter((tag) => tag !== name),
            });
        } else {
            setLocationData({
                ...locationData,
                tags: [...locationData.tags, name],
            });
        }
    };

    const getLocationCategories = async (filter) => {
        const data = await fetchCategories(filter);
        setAllLocationCategories(mapDataToDropdown(data.entities.locationCategories, "nameAr"));
        return mapDataToDropdown(data.entities.locationCategories, "nameAr");
    };

    const getClients = async (filter) => {
        const data = await fetchOrganizations(filter);
        setAllClients(mapDataToDropdown(data.entities.organizations, "name"));
        return mapDataToDropdown(data.entities.organizations, "name");
    };

    const matchArabicName = async () => {
        try {
            const data = {
                lat: locationData.lat,
                lang: locationData.lang,
                clientId: locationData.client.value,
                matcher: "name_ar",
                name: locationData.nameAr,
            };
            const response = await checkLocationMatches(data);
            if (response.data) {
                setSimilarArabicLocations(response.data);
            } else {
                setSimilarArabicLocations([]);
            }
        } catch (err) {
            return null;
        }
    };

    const matchEnglishName = async () => {
        try {
            const data = {
                lat: locationData.lat,
                lang: locationData.lang,
                clientId: locationData.client.value,
                matcher: "name_en",
                name: locationData.nameEn,
            };
            const response = await checkLocationMatches(data);
            if (response.data) {
                setSimilarEnglishLocations(response.data);
            } else {
                setSimilarEnglishLocations([]);
            }
        } catch (err) {
            return null;
        }
    };

    const clearLocationData = () => {
        setLocationData({
            id: null,
            client: null,
            locationName: "",
            googleLocationDetails: "",
            nameAr: "",
            nameEn: "",
            street: "",
            description: "",
            category: null,
            clientCode: "",
            tags: [],
            lat: 0,
            lang: 0,
            gov: "",
            city: "",
            area: "",
        });
    };

    const onSubmit = async () => {
        isEdit ? handleEditLocation() : handleAddLocation();
    };

    const handleAddLocation = async () => {
        trackAnalytics(`Locations_profile_submit_creation`);
        try {
            const response = await addNewLocation(mapLocationToAPI(locationData));
            const { attributes } = Object.values(response.entities.locations)[0];
            onSuccess(attributes);
            clearLocationData();
            setFormErrors([]);
        } catch (error) {
            setFormErrors(error?.errors);
        }
    };

    const handleEditLocation = async () => {
        const changedData = changedForm(existingLocationData, locationData);
        trackAnalytics(`Locations_submit_edit`, changedData);
        try {
            await editLocation(mapLocationToAPI(locationData));
            clearLocationData();
            onSuccess();
            setFormErrors([]);
        } catch (error) {
            setFormErrors(error?.errors);
        }
    };

    const onCopy = () => {
        navigator.clipboard.writeText(`
        id: ${locationData.id},
        Client name: ${locationData.client?.label},
        Google location details: ${locationData.googleLocationDetails},
        Client Location Name (Arabic): ${locationData.nameAr}
        `);
    };

    useEffect(() => {
        getClients();
        getLocationCategories();
    }, []);

    useEffect(() => {
        if (isEdit || readOnly) {
            setLocationData(existingLocationData);
        }
    }, [isEdit, existingLocationData, readOnly]);

    useEffect(() => {
        if (locationData.lat !== 0 && locationData.nameAr?.length > 0 && !isEdit && !readOnly) {
            matchArabicName();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationData.lat, locationData.nameAr]);

    useEffect(() => {
        if (locationData.lat !== 0 && locationData.nameEn?.length > 0 && !isEdit && !readOnly) {
            matchEnglishName();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationData.lat, locationData.nameEn]);

    useEffect(() => {
        if (
            locationData.client === null ||
            locationData.googleLocationDetails === null ||
            locationData.nameAr === "" ||
            locationData.nameEn === "" ||
            locationData.street === "" ||
            locationData.category === null ||
            locationData.street === ""
        ) {
            setIsFormValid(false);
        } else {
            setIsFormValid(true);
        }
    }, [locationData]);

    return (
        <Container>
            <Body>
                <Form>
                    <InputContainer>
                        {!readOnly && (
                            <TitleContainer>
                                <Title>Client Name</Title>
                                <Info>
                                    Select client name first and then fill the location details
                                </Info>
                            </TitleContainer>
                        )}
                        {readOnly && (
                            <LocationDetails>
                                <LocationId>#{locationData.id}</LocationId>
                                <IconsContainer>
                                    {isAuthorizedToEdit && (
                                        <BorderColorIcon onClick={onSwitchToEdit} />
                                    )}
                                    <ContentCopyIcon onClick={onCopy} />
                                </IconsContainer>
                            </LocationDetails>
                        )}
                        <SelectWithSearch
                            options={allClients}
                            value={locationData.client}
                            name="selectClient"
                            label="Client name"
                            placeholder="Select Client Name"
                            handleChange={(value) =>
                                setLocationData({
                                    ...locationData,
                                    client: value,
                                })
                            }
                            handleFilter={(filter) => getClients(filter)}
                            isEditable
                            required
                            disable={readOnly || isEdit}
                        />
                    </InputContainer>
                    {!readOnly && (
                        <InputContainer>
                            <TitleContainer>
                                <Title>Location Details</Title>
                                <Info>
                                    Location details will be auto-filled once you pin a location on
                                    the map
                                </Info>
                            </TitleContainer>
                            <GoogleMapSearchComponent
                                handleSearchInput={handleSearchInputData}
                                locationName={locationData.locationName}
                                clearLocation={handleClearLocation}
                            />
                            <Info
                                marginBottom="7px"
                                color="#006BB4"
                            >
                                How to Search?
                            </Info>
                            <SearchTagsContainer>
                                <SearchTag>Paste lat & long</SearchTag>
                                -Or-
                                <SearchTag>Write location name</SearchTag>
                                -Or-
                                <SearchTag>Paste location link</SearchTag>
                            </SearchTagsContainer>
                        </InputContainer>
                    )}

                    <TextArea
                        label="Google location details"
                        name="googleLocationDetails"
                        placeholder="will be auto-filled once you pin a location on the map"
                        type="text"
                        value={locationData.googleLocationDetails}
                        required
                        isEditable={false}
                        disabledWithBorder
                    />

                    <Input
                        disableWithBorder={false}
                        label="Client Location Name (Arabic)"
                        name="nameAr"
                        placeholder="EX: Hyper one"
                        type="text"
                        value={locationData.nameAr}
                        onChange={(e) => handleLocationDetailsChange(e)}
                        required
                        isEditable={!readOnly}
                        errorMessage={
                            similarArabicLoactions.length > 0
                                ? "You have the same location name within 1 km near you. Enter a different name or check out the saved pins around."
                                : null
                        }
                    />

                    <Input
                        disableWithBorder={false}
                        label="Client Location Name (English)"
                        name="nameEn"
                        placeholder="EX: Hyper one"
                        type="text"
                        value={locationData.nameEn}
                        onChange={(e) => handleLocationDetailsChange(e)}
                        required
                        isEditable={!readOnly}
                        errorMessage={
                            similarEnglishLocations.length > 0
                                ? "You have the same location name within 1 km near you. Enter a different name or check out the saved pins around."
                                : null
                        }
                    />

                    <Input
                        disableWithBorder={false}
                        label="Street - Building/Plot  No"
                        name="street"
                        type="text"
                        value={locationData.street}
                        onChange={(e) => handleLocationDetailsChange(e)}
                        required
                        isEditable={!readOnly}
                    />

                    <Input
                        disableWithBorder={false}
                        label="Description"
                        name="description"
                        type="text"
                        value={locationData.description}
                        onChange={(e) => handleLocationDetailsChange(e)}
                        isEditable={!readOnly}
                    />

                    <SelectWithSearch
                        options={allLocationCategories}
                        value={locationData.category}
                        handleChange={(value) =>
                            setLocationData({
                                ...locationData,
                                category: value,
                            })
                        }
                        handleFilter={(filter) => getLocationCategories(filter)}
                        label="Category"
                        name="category"
                        placeholder="Select Category Name"
                        isEditable
                        required
                        disable={readOnly}
                    />

                    <Input
                        disableWithBorder={false}
                        label="Client location’s Unique Identifier/code "
                        name="clientCode"
                        placeholder="EX: 8954243"
                        type="text"
                        value={locationData.clientCode}
                        onChange={(e) => handleLocationDetailsChange(e)}
                        isEditable={!readOnly}
                    />

                    <InputContainer>
                        <StyledLabel>
                            Location type
                            <StyledRequired>*</StyledRequired>
                        </StyledLabel>
                        <TagsContainer>
                            <Tag onClick={() => handleLocationTagChange("pickup")}>
                                <EuiCheckbox
                                    label="Pickup"
                                    name="pickup"
                                    checked={locationData.tags.includes("pickup")}
                                    disabled={readOnly}
                                />
                            </Tag>
                            <Tag onClick={() => handleLocationTagChange("dropoff")}>
                                <EuiCheckbox
                                    label="Drop-off"
                                    name="dropoff"
                                    checked={locationData.tags.includes("dropoff")}
                                    disabled={readOnly}
                                />
                            </Tag>
                        </TagsContainer>
                    </InputContainer>
                </Form>

                <MapContainer>
                    {isLoaded && <GoogleMapComponent
                        isMarkerShown
                        containerElement={<div style={{ height: "100%", width: "100%" }} />}
                        setMapLocationData={handleSetMapLocationData}
                        lat={locationData.lat}
                        lang={locationData.lang}
                        isOpen
                        similarLocations={[...similarArabicLoactions, ...similarEnglishLocations]}
                        readOnly={readOnly}
                        isLocationSelectedFromSearch={isLocationSelectedFromSearch}
                        setIsLocationSelectedFromSearch={setIsLocationSelectedFromSearch}
                    />}
                </MapContainer>
            </Body>

            {!readOnly && (
                <Footer>
                    {typeof formErrors === "string" ? (
                        <ErrorsContainer>
                            <ErrorMessage>
                                <WarningAmberOutlinedIcon />
                                {formErrors}
                            </ErrorMessage>
                        </ErrorsContainer>
                    ) : typeof formErrors === "object" ? (
                        Object.keys(formErrors).map((key, index) => (
                            <ErrorMessage key={index}>
                                <WarningAmberOutlinedIcon />
                                {`${key}: ${formErrors[key]}`}
                            </ErrorMessage>
                        ))
                    ) : typeof formErrors === "array" ? (
                        formErrors.length > 0 && (
                            <ErrorsContainer>
                                {formErrors.map((error, index) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <ErrorMessage key={index}>
                                        <WarningAmberOutlinedIcon />
                                        {error}
                                    </ErrorMessage>
                                ))}
                            </ErrorsContainer>
                        )
                    ) : null}
                    <ButtonsContainer>
                        <Button
                            onChange={onCancel}
                            name="Cancel"
                            btnStyle="secondBtn"
                        />
                        <Button
                            onChange={onSubmit}
                            name={isEdit ? "Save" : "Add"}
                            btnStyle={`primaryBtn ${!isFormValid && "disabled"}`}
                            disabled={!isFormValid}
                        />
                    </ButtonsContainer>
                </Footer>
            )}
        </Container>
    );
}

export default CreateLocation;
